import { apiClient, getErrorMessage } from '@/lib/apiClient';
import type {
  RuleDefinitionDto,
  RuleContextEntityDto,
  TenantRuleDto,
  CreateTenantRuleDto,
  UpdateTenantRuleDto,
  RuleContextSchemaDto,
  RuleSimulationRequest,
  RuleSimulationResult,
  RuleValidationRequest,
  RuleValidationResult,
} from '@/types/core/Rules';

/**
 * Result of rule key uniqueness check
 */
export interface RuleKeyCheckResult {
  exists: boolean;
  isUnique: boolean;
}

/**
 * Result of bulk reorder operation
 */
export interface ReorderRulesResult {
  success: boolean;
  updatedCount: number;
}

/**
 * Rule Service
 * Service for managing business rules (system, tenant, orgUnit)
 */
export class RuleService {
  private readonly baseUrl = '/api/rules';

  /**
   * Get context schema for rule building (fields, types, operators)
   */
  async getContextSchema(entityName: string, orgUnitId?: string): Promise<RuleContextSchemaDto> {
    try {
      const params: Record<string, string> = { entityName };
      if (orgUnitId) {
        params.orgUnitId = orgUnitId;
      }
      const response = await apiClient.get<RuleContextSchemaDto>(`${this.baseUrl}/context-schema`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching context schema:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get system rules (read-only, from DSL)
   */
  /**
   * The entities a tenant rule can target (those with a declared rule context), with their
   * module — the option list of the form's Module/Entity pickers.
   */
  async getContextEntities(): Promise<RuleContextEntityDto[]> {
    try {
      const response = await apiClient.get<RuleContextEntityDto[]>(`${this.baseUrl}/context-entities`);
      return response.data || [];
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getSystemRules(entityName: string, moduleName: string): Promise<RuleDefinitionDto[]> {
    try {
      const response = await apiClient.get<RuleDefinitionDto[]>(`${this.baseUrl}/system-rules`, {
        params: { entityName, moduleName },
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching system rules:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get a single system rule by ID (read-only)
   */
  async getSystemRule(id: string, entityName: string, moduleName: string): Promise<RuleDefinitionDto> {
    try {
      const response = await apiClient.get<RuleDefinitionDto>(`${this.baseUrl}/system-rules/${id}`, {
        params: { entityName, moduleName },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching system rule:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get tenant or orgUnit rules (custom rules)
   */
  async getCustomRules(
    entityName: string,
    moduleName: string,
    orgUnitId?: string
  ): Promise<TenantRuleDto[]> {
    try {
      const params: Record<string, string> = { entityName, moduleName };
      if (orgUnitId) {
        params.orgUnitId = orgUnitId;
      }
      const response = await apiClient.get<TenantRuleDto[]>(`${this.baseUrl}/custom-rules`, {
        params,
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching custom rules:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get a single custom rule by ID
   */
  async getCustomRule(id: string): Promise<TenantRuleDto> {
    try {
      const response = await apiClient.get<TenantRuleDto>(`${this.baseUrl}/custom-rules/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching custom rule:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get effective rules (system + tenant + orgUnit merged)
   */
  async getEffectiveRules(
    entityName: string,
    moduleName: string,
    orgUnitId?: string
  ): Promise<RuleDefinitionDto[]> {
    try {
      const params: Record<string, string> = { entityName, moduleName };
      if (orgUnitId) {
        params.orgUnitId = orgUnitId;
      }
      const response = await apiClient.get<RuleDefinitionDto[]>(`${this.baseUrl}/effective-rules`, {
        params,
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching effective rules:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Simulate rule execution against sample data
   */
  async simulateRule(request: RuleSimulationRequest): Promise<RuleSimulationResult> {
    try {
      const response = await apiClient.post<RuleSimulationResult>(
        `${this.baseUrl}/simulate`,
        request
      );
      return response.data;
    } catch (error) {
      console.error('Error simulating rule:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Validate a rule definition before saving
   */
  async validateRule(request: RuleValidationRequest): Promise<RuleValidationResult> {
    try {
      const response = await apiClient.post<RuleValidationResult>(
        `${this.baseUrl}/custom-rules/validate`,
        request
      );
      return response.data;
    } catch (error) {
      console.error('Error validating rule:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get transitions for a specific rule
   */
  async getTransitionsForRule(
    ruleKey: string,
    entityName: string,
    moduleName: string,
    orgUnitId?: string
  ): Promise<TransitionDto[]> {
    try {
      const params: Record<string, string> = { entityName, moduleName };
      if (orgUnitId) {
        params.orgUnitId = orgUnitId;
      }
      const response = await apiClient.get<TransitionDto[]>(
        `${this.baseUrl}/${ruleKey}/transitions`,
        { params }
      );
      return response.data || [];
    } catch (error) {
      console.error('Error fetching transitions for rule:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Create a new custom rule (tenant or orgUnit)
   */
  async createCustomRule(input: CreateTenantRuleDto): Promise<TenantRuleDto> {
    try {
      const response = await apiClient.post<TenantRuleDto>(
        `${this.baseUrl}/custom-rules`,
        input
      );
      return response.data;
    } catch (error) {
      console.error('Error creating custom rule:', error);
      throw error; // Re-throw original error for proper validation error extraction
    }
  }

  /**
   * Update an existing custom rule
   */
  async updateCustomRule(id: string, input: UpdateTenantRuleDto): Promise<TenantRuleDto> {
    try {
      const response = await apiClient.put<TenantRuleDto>(
        `${this.baseUrl}/custom-rules/${id}`,
        input
      );
      return response.data;
    } catch (error) {
      console.error('Error updating custom rule:', error);
      throw error; // Re-throw original error for proper validation error extraction
    }
  }

  /**
   * Delete a custom rule
   */
  async deleteCustomRule(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.baseUrl}/custom-rules/${id}`);
    } catch (error) {
      console.error('Error deleting custom rule:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Activate a draft rule (make it Active)
   */
  async activateRule(id: string): Promise<TenantRuleDto> {
    try {
      const response = await apiClient.post<TenantRuleDto>(
        `${this.baseUrl}/custom-rules/${id}/activate`
      );
      return response.data;
    } catch (error) {
      console.error('Error activating rule:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Deactivate an active rule (make it Draft)
   */
  async deactivateRule(id: string): Promise<TenantRuleDto> {
    try {
      const response = await apiClient.post<TenantRuleDto>(
        `${this.baseUrl}/custom-rules/${id}/deactivate`
      );
      return response.data;
    } catch (error) {
      console.error('Error deactivating rule:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Check if a rule key is unique for the given scope
   */
  async checkRuleKeyUniqueness(
    entityName: string,
    moduleName: string,
    ruleKey: string,
    orgUnitId?: string,
    excludeId?: string
  ): Promise<RuleKeyCheckResult> {
    try {
      const params: Record<string, string> = { entityName, moduleName, ruleKey };
      if (orgUnitId) {
        params.orgUnitId = orgUnitId;
      }
      if (excludeId) {
        params.excludeId = excludeId;
      }
      const response = await apiClient.get<RuleKeyCheckResult>(
        `${this.baseUrl}/check-rule-key`,
        { params }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking rule key uniqueness:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Bulk reorder rules (update priorities)
   */
  async reorderRules(
    rules: Array<{ id: string; priority: number }>
  ): Promise<ReorderRulesResult> {
    try {
      const response = await apiClient.put<ReorderRulesResult>(
        `${this.baseUrl}/custom-rules/reorder`,
        { rules }
      );
      return response.data;
    } catch (error) {
      console.error('Error reordering rules:', error);
      throw new Error(getErrorMessage(error));
    }
  }
}

export interface TransitionDto {
  key: string;
  from: string;
  to: string;
  description?: string;
}

// Export singleton instance
export const ruleService = new RuleService();

